import { useState, useEffect, useMemo } from "react";

interface DetailPageMiniImagesProps {
  images: string[];
  videos: string[];
  onImageSelect: (imageUrl: string) => void;
  onVideoSelect: (videoUrl: string) => void;
  selectedImage: string;
  selectedVideo: string;
}

const DetailPageMiniImages = ({
  images,
  videos,
  onImageSelect,
  onVideoSelect,
  selectedImage,
  selectedVideo,
}: DetailPageMiniImagesProps) => {
  // Combine images and videos into a single array for carousel
  const allMedia = useMemo(
    () => [
      ...images.map((img) => ({ type: "image" as const, url: img })),
      ...videos.map((vid) => ({ type: "video" as const, url: vid })),
    ],
    [images, videos],
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  // Update current index when selectedImage or selectedVideo changes
  useEffect(() => {
    if (selectedImage) {
      const index = allMedia.findIndex(
        (item) => item.type === "image" && item.url === selectedImage,
      );
      if (index !== -1) setCurrentIndex(index);
    } else if (selectedVideo) {
      const index = allMedia.findIndex(
        (item) => item.type === "video" && item.url === selectedVideo,
      );
      if (index !== -1) setCurrentIndex(index);
    }
  }, [selectedImage, selectedVideo, allMedia]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    const media = allMedia[index];
    if (media.type === "image") {
      onImageSelect(media.url);
    } else {
      onVideoSelect(media.url);
    }
  };

  const handleMediaClick = () => {
    const media = allMedia[currentIndex];
    if (media.type === "image") {
      onImageSelect(media.url);
    } else {
      onVideoSelect(media.url);
    }
  };
  return (
    <>
      <div className=" display-max">
        <div className=" d-flex justify-content-start align-items-start gap-2 mr-2">
          <div className=" mt-4">
            {images.length > 0 &&
              images.map((image) => (
                <div
                  key={image}
                  className={`mini-pro-img mb-1 ${selectedImage === image ? "selected" : ""} ${image === "string" ? "d-none" : ""}`}
                  onClick={() => onImageSelect(image)}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedImage === image
                        ? "2px solid #007bff"
                        : "2px solid transparent",
                    width: "60px",
                    height: "60px",
                  }}
                >
                  <img
                    src={image}
                    alt="img-blur-shadow"
                    className="mini-product-img p-1"
                    loading="lazy"
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
              ))}
            {videos.length > 0 &&
              videos.map((video) => (
                <div
                  key={video}
                  className={`mini-pro-img mb-1 ${selectedVideo === video ? "selected" : ""} ${video === "string" ? "d-none" : ""}`}
                  onClick={() => onVideoSelect(video)}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedVideo === video
                        ? "2px solid #007bff"
                        : "2px solid transparent",
                    width: "60px",
                    height: "60px",
                  }}
                >
                  <video
                    src={video}
                    className="mini-product-img p-1"
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className=" display-mini display-mob">
        {allMedia.length > 0 && (
          <div
            className="position-relative bg-gray"
            style={{ height: "80vh", overflow: "hidden", width: "100vw" }}
          >
            {/* Carousel Container */}
            <div
              className="d-flex align-items-center"
              style={{
                transform: `translateX(-${currentIndex * 100}vw)`,
                transition: "transform 0.3s ease-in-out",
                height: "100%",
                width: `${allMedia.length * 100}vw`,
              }}
            >
              {allMedia.map((media, index) => (
                <div
                  key={`${media.type}-${index}`}
                  className="position-relative"
                  style={{
                    minWidth: "100vw",
                    width: "100vw",
                    height: "100vh",
                    flexShrink: 0,
                  }}
                  onClick={handleMediaClick}
                >
                  {media.type === "image" ? (
                    <img
                      src={media.url}
                      alt={`Product image ${index + 1}`}
                      className="w-100 h-100"
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                      }}
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/placeholder-image.jpg";
                      }}
                    />
                  ) : (
                    <video
                      src={media.url}
                      className="w-100 h-100"
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                      }}
                      controls
                      autoPlay={currentIndex === index}
                      loop
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {allMedia.length > 1 && (
              <>
                <button
                  className="position-absolute top-50 start-0 translate-middle-y btn btn-light rounded-circle p-2 m-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  style={{
                    zIndex: 10,
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-label="Previous"
                >
                  <i
                    className="bi bi-chevron-left"
                    style={{ fontSize: "20px" }}
                  ></i>
                </button>
                <button
                  className="position-absolute top-50 end-0 translate-middle-y btn btn-light rounded-circle p-2 m-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  style={{
                    zIndex: 10,
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-label="Next"
                >
                  <i
                    className="bi bi-chevron-right"
                    style={{ fontSize: "20px" }}
                  ></i>
                </button>
              </>
            )}

            {/* Carousel Indicators */}
            {allMedia.length > 1 && (
              <div
                className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1 mb-3"
                style={{ zIndex: 10 }}
              >
                {allMedia.map((_, index) => (
                  <button
                    key={index}
                    className={`btn rounded-circle p-0 ${
                      currentIndex === index ? "bg-info" : "bg-secondary"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSlide(index);
                    }}
                    style={{
                      width: "10px",
                      height: "10px",
                      border: "none",
                      opacity: currentIndex === index ? 1 : 0.5,
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Slide Counter */}
            {allMedia.length > 1 && (
              <div
                className="position-absolute top-0 end-0 m-2 bg-dark bg-opacity-50 text-white px-2 py-1 rounded"
                style={{ zIndex: 10, fontSize: "12px" }}
              >
                {currentIndex + 1} / {allMedia.length}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DetailPageMiniImages;
